/** Dialog ເລືອກຖັນສຳລັບພິມຕາຕະລາງຜົນຕອບ */

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFormResponsesApi } from "@/modules/forms/presentation/api/client";
import type { FormResponsesApi } from "@/modules/forms/presentation/api/form-responses-api";
import type { FormFieldDTO } from "@/modules/forms/presentation/api/types";
import { printFormResponsesA4 } from "@/modules/forms/presentation/lib/print-form-responses-a4";

type FormResponsesPrintPdfDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  fields: FormFieldDTO[];
  filt?: string;
  responsesApi?: FormResponsesApi;
};

export function FormResponsesPrintPdfDialog({
  open,
  onOpenChange,
  formId,
  fields,
  filt,
  responsesApi = adminFormResponsesApi,
}: FormResponsesPrintPdfDialogProps) {
  const printableFields = useMemo(
    () =>
      [...fields]
        .filter((f) => f.type !== "section")
        .sort((a, b) => a.order - b.order),
    [fields],
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    printableFields.map((f) => f.id),
  );

  useEffect(() => {
    if (open) {
      setSelectedIds(printableFields.map((f) => f.id));
    }
  }, [open, printableFields]);

  const toggleId = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(printableFields.map((f) => f.id));
  }, [printableFields]);

  const selectNone = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const runPrint = useCallback(() => {
    const order = printableFields.map((f) => f.id);
    if (selectedIds.length === 0) {
      toast.error("ເລືອກຢ່າງໜ້ອຍໜຶ່ງຖັນ");
      return;
    }
    const chosen = order.filter((id) => selectedIds.includes(id));
    if (chosen.length === 0) {
      toast.error("ເລືອກຢ່າງໜ້ອຍໜຶ່ງຖັນ");
      return;
    }
    void toast.promise(
      (async () => {
        const payload = await responsesApi.fetchResponsesPrintData(formId, {
          filt,
          columnFieldIds: chosen,
        });
        await printFormResponsesA4(payload);
        onOpenChange(false);
      })(),
      {
        loading: "ກຳລັງເຕັມຂໍ້ມູນພິມ...",
        success: "ເປີດກະດານພິມແລ້ວ ບັນທຶກວາງ PDF ຈາກນັ້ນຕໍ່",
        error: "ພິມບໍ່ສຳເລັດ — ກວດວ່າບໍ່ຖືກບລັອກ popup",
      },
    );
  }, [filt, formId, onOpenChange, printableFields, responsesApi, selectedIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ພິມ / PDF (A4) — ເລືອກຖັນ</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={selectAll}>
            ເລືອກທັງໝົດ
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={selectNone}
          >
            ຍົກເລີກທັງໝົດ
          </Button>
        </div>
        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="flex flex-col gap-3 pb-2">
            {printableFields.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຟິວທີ່ພິມໄດ້ (ລະຫັດພາກກຳນົດບໍ່ນັບເປັນຖັນ)
              </p>
            ) : (
              printableFields.map((f) => {
                const cid = `print-col-${f.id}`;
                return (
                  <div key={f.id} className="flex items-start gap-2">
                    <Checkbox
                      id={cid}
                      checked={selectedIds.includes(f.id)}
                      onCheckedChange={(v) => toggleId(f.id, Boolean(v))}
                    />
                    <Label
                      htmlFor={cid}
                      className="cursor-pointer font-normal leading-snug"
                    >
                      {f.label}
                    </Label>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={printableFields.length === 0}
            onClick={() => runPrint()}
          >
            ຕໍ່ ແລະ ເປີດພິມ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
